<script lang="ts" setup>import { computed as _computed, ref as _ref } from 'vue';

import type GiftTemplate from '@consumer/types/GiftTemplate'

import { useGift } from '@consumer/composables/gift'
import { useListeners } from '@shared/vue'
import { titleize } from 'inflection'

import { useElementSize } from '@vueuse/core'

import '@consumer/styles/giftly_image.scss'
import newMiscGiftlyLogoIcon from '@consumer/assets/icons/new-misc/giftly-logo.svg?svgicon'

withDefaults(defineProps<{
  gift: GiftTemplate
  descriptionIcon?: string
}>(), {  })

const emit = defineEmits<{
  clickAmount: []
  clickBackground: []
  clickItem: []
  clickPlace: []
}>()

const isClickable = useListeners(listeners => ({
  amount: typeof listeners.onClickAmount === 'function',
  background: typeof listeners.onClickBackground === 'function',
  item: typeof listeners.onClickItem === 'function',
  place: typeof listeners.onClickPlace === 'function',
} as const))

const { formattedAmount, placeCount, hasMultiplePlaces, tagline } = useGift(() => __props.gift as any)

const backgroundImageUrl = _computed(() => __props.gift.giftlyMetadata?.backgroundImageUrl)

// Scale the image
const cardEl = _ref<HTMLElement>()
const { width: wrapperWidth } = useElementSize(() => cardEl.value)
watch(
  wrapperWidth,
  (width) => {
    if (!cardEl.value) return

    // Set the font size to 1px as the basis when rendering at 360px, allowing
    // us to use em as if they were px.
    cardEl.value.style.setProperty('--giftly-font-size', `${width / 360}px`)
  },
)
</script>

<template>
  <div
    ref="cardEl"
    class="giftly-image-v2 giftly-image-wrapper select-none"
    :class="{ 'giftly-image-v2--no-background': !backgroundImageUrl }"
  >
    <img
      v-if="backgroundImageUrl"
      :src="backgroundImageUrl"
      class="giftly-image-v2--background-image"
      :class="isClickable.background && 'clickable'"
      name="nc_giftly_background"
      loading="lazy"
      :alt="`${formattedAmount} ${tagline} Gift Card`"
      @click="emit('clickBackground')"
    >

    <div v-if="formattedAmount" class="giftly-image-v2--header giftly-image-v2--responsive">
      <i class="giftly-image-v2--giftly-logo">
        <GIcon :svg="newMiscGiftlyLogoIcon" color="white" size=""/>
      </i>
      <div
        class="giftly-image-v2--giftly-amount"
        :class="isClickable.amount && 'clickable'"
        name="nc_giftly_amount"
        @click="emit('clickAmount')"
      >
        {{ formattedAmount }}
      </div>
    </div>

    <div class="giftly-image-v2--description">
      <template v-if="gift.amount">
        <div class="giftly-image-v2--description--item-and-place giftly-image-v2--responsive">
          <div
            class="giftly-image-v2--description--item-and-place--item"
            :class="isClickable.item && 'clickable'"
            name="nc_giftly_item"
            @click="emit('clickItem')"
          >
            {{ gift.itemDescription ? titleize(gift.itemDescription) : 'Anything' }}
          </div>
          <div
            class="giftly-image-v2--description--item-and-place--place"
            name="nc_giftly_place"
            @click="emit('clickPlace')"
          >
            <div class="giftly-image-v2--description--item-and-place--place--name">
              {{ tagline }}
            </div>
            <div v-if="hasMultiplePlaces">
              +{{ placeCount - 1 }} more
            </div>
          </div>
        </div>

        <div v-if="descriptionIcon" class="giftly-image-v2--description--interact-button">
          <GIcon :icon="descriptionIcon" size="16px" color="white"/>
        </div>
      </template>
      <div v-else class="giftly-image-v2--description--no-content">
        Create My Own
      </div>
    </div>

    <slot/>
  </div>
</template>
